import { useEffect, useRef } from 'react';
import {
    sortedChildren, findBranch, getAncestors, getSiblings, isRootBranch,
} from './branchTree';
import { macChords, coarsePointer } from './platform';

/**
 * BranchNavigator — the V3 scenario tree.
 *
 * It lives in the SAME space as the timeline strip (bottom of the screen, above
 * nothing — the 3D viewport stays visible above it). The space switches:
 *
 *   scenario tree  ── Edit / double-click a branch ──▶  that branch's timeline
 *   scenario tree  ◀── BACK (in the timeline ruler) ──  branch timeline
 *
 * Three columns, exactly as specified:
 *   FROM            where the selected branch came from (empty for the root)
 *   CURRENT · SIBLINGS  the selected branch plus its brothers, in priority
 *                       order — the number is editable, 1 is the default
 *   POSSIBLE NEXT   the sub-branches this one can continue into
 *
 * Selecting a row only moves the selection. The buttons on the selected row act
 * on it: ✂ clip it to its last keyframe, ✎ open its timeline, ⧉ copy its
 * timeline, ⎘ paste a copied timeline INTO it, ▲ ▼ re-prioritise, ✕ remove.
 *
 * Both lists end with a way to grow the tree: POSSIBLE NEXT has "+ Add
 * sub-branch" (continues from the END of the selected branch) and CURRENT ·
 * SIBLINGS has "+ Add sibling branch" (continues from the same point as the
 * selected branch — its parent).
 */
export default function BranchNavigator({
    tree,
    selectedId,
    openId = null,          // branch currently loaded in the timeline (marked "in timeline")
    chosenIds = null,       // Set of branch ids the user picked for their master path
    onSelect = () => {},
    onEdit = () => {},
    onAdd = () => {},
    onClip = () => {},
    onCopyTimeline = () => {},    // put this branch's timeline on the clipboard
    onPasteTimeline = () => {},   // paste the copied timeline INTO this branch
    onAddSibling = () => {},      // new branch continuing from the same point
    onDuplicate = () => {},       // a copy of THIS branch: timeline, name and place
    clipboardBranch = null,       // { id, name } of the branch on the clipboard, if any
    onMove = () => {},
    onSetOrder = () => {},
    onRename = () => {},
    onRemove = () => {},
    height = 230,           // total panel height (strip + ruler space)
    onHeightChange = null,  // (px) => void — drag the handle to resize
    renameSignal = 0,       // Shift+R in App: bump to focus the selected row's name input
    t = (k) => k,
}) {
    const dragRef = useRef(null);
    const nameInputRef = useRef(null);

    // A rename request from anywhere (Shift+R on the pitch): grab the selected
    // row's name input and pre-select the text so typing replaces the name.
    useEffect(() => {
        if (renameSignal > 0 && nameInputRef.current) {
            nameInputRef.current.focus();
            nameInputRef.current.select();
        }
    }, [renameSignal]);

    if (!tree) return null;

    const selected = findBranch(tree, selectedId) || tree;
    const siblings = getSiblings(tree, selected.id);
    const ancestors = getAncestors(tree, selected.id);
    const children = sortedChildren(selected);
    const selectedIsRoot = isRootBranch(tree, selected.id);

    // ── Resize handle: same feel as the timeline strip's drag handle ──
    const startResize = (e) => {
        if (!onHeightChange) return;
        const startY = e.clientY;
        const startH = height;
        const onMoveEv = (ev) => {
            const h = Math.round(startH - (ev.clientY - startY));
            const max = Math.max(240, window.innerHeight - 260); // keep the pitch visible
            onHeightChange(Math.min(max, Math.max(98, h)) + 0);
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMoveEv);
            window.removeEventListener('pointerup', onUp);
            dragRef.current = null;
        };
        window.addEventListener('pointermove', onMoveEv);
        window.addEventListener('pointerup', onUp);
        dragRef.current = true;
        e.preventDefault();
    };

    const btn = (label, title, onClick, extra = {}) => (
        <button
            type="button"
            title={macChords(title)}
            onClick={(e) => { e.stopPropagation(); if (onClick) onClick(); }}
            onDoubleClick={(e) => e.stopPropagation()}
            style={{
                width: 26, height: 26, flexShrink: 0, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 5, fontSize: 13, fontWeight: 800, lineHeight: 1,
                background: '#1e293b', color: '#cbd5e1', border: '1px solid #334155',
                padding: 0, ...extra,
            }}
        >{label}</button>
    );

    /** One branch row: priority box, name, and (for the selected row) the 4 buttons. */
    const row = (branch, { isRowSelected, showActions }) => {
        const hasChildren = sortedChildren(branch).length > 0;
        const inTimeline = openId === branch.id;
        // The user's OWN pick for this sibling set — part of the path the master
        // timeline plays. Shown with a green edge so the chosen path is readable
        // wherever you are in the tree (the selection is not saved with the file).
        const isChosen = !!(chosenIds && chosenIds.has(branch.id));
        return (
            <div
                key={branch.id}
                role="button"
                tabIndex={0}
                onClick={() => (coarsePointer() && isRowSelected
                    // TAP AGAIN = THE NEXT VERB, the same ladder the pitch uses:
                    // on a phone there is no double-click to lean on, and the only
                    // other route into this branch's timeline is a 32px ✎ a thumb
                    // has to find. A plain tap on the row you are already on was
                    // inert, so nothing is lost — it now goes somewhere.
                    ? onEdit(branch.id) : onSelect(branch.id))}
                onDoubleClick={() => onEdit(branch.id)}
                onKeyDown={(e) => { if (e.key === 'Enter') onEdit(branch.id); }}
                title={isChosen ? `${t('branch.rowTitle')} — ${t('branch.chosen')}` : t('branch.rowTitle')}
                style={{
                    display: 'flex', alignItems: 'center', gap: 6, minHeight: 44,
                    padding: '4px 6px', borderRadius: 7, cursor: 'pointer',
                    background: isRowSelected ? 'rgba(99,102,241,0.22)' : 'rgba(15,23,42,0.75)',
                    border: `1px solid ${isRowSelected ? '#6366f1' : (isChosen ? '#15803d' : '#1e293b')}`,
                    boxShadow: isChosen ? 'inset 3px 0 0 0 #22c55e' : 'none',
                }}
            >
                <input
                    type="number"
                    min="1"
                    aria-label={t('branch.order')}
                    title={t('branch.orderHint')}
                    value={branch.order ?? 1}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onSetOrder(branch.id, e.target.value)}
                    style={{
                        width: 34, height: 26, flexShrink: 0, textAlign: 'center',
                        background: '#0b1120', color: isRowSelected ? '#e0e7ff' : '#94a3b8',
                        border: `1px solid ${isRowSelected ? '#6366f1' : '#334155'}`,
                        borderRadius: 5, fontSize: 12, fontWeight: 800, padding: 0,
                    }}
                />
                {isRowSelected ? (
                    <input
                        ref={nameInputRef}
                        type="text"
                        aria-label={t('branch.name')}
                        value={branch.name}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => onRename(branch.id, e.target.value)}
                        // The ⇧R toast promises "Enter keeps it" — make it
                        // true: Enter (or Esc) ends the rename and hands the
                        // keyboard back to the scene (the name itself already
                        // commits on every keystroke).
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === 'Escape') {
                                e.preventDefault();
                                e.currentTarget.blur();
                            }
                        }}
                        style={{
                            flex: 1, minWidth: 60, height: 26, background: '#0b1120',
                            color: '#e2e8f0', border: '1px solid #334155', borderRadius: 5,
                            fontSize: 12.5, fontWeight: 700, padding: '0 7px',
                        }}
                    />
                ) : (
                    <span style={{
                        flex: 1, minWidth: 0, color: '#e2e8f0', fontSize: 12.5, fontWeight: 600,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{branch.name}</span>
                )}
                {branch.order === 1 && (
                    <span style={{
                        flexShrink: 0, fontSize: 9, fontWeight: 800, letterSpacing: '0.05em',
                        color: '#bbf7d0', background: 'rgba(6,78,59,0.85)', border: '1px solid #15803d',
                        borderRadius: 4, padding: '1px 4px',
                    }}>{t('branch.defaultTag')}</span>
                )}
                {hasChildren && (
                    <span title={t('branch.subBranches')} style={{
                        flexShrink: 0, fontSize: 10, color: '#94a3b8', background: '#0b1120',
                        border: '1px solid #334155', borderRadius: 4, padding: '1px 5px',
                    }}>{sortedChildren(branch).length}</span>
                )}
                {inTimeline && (
                    <span title={t('branch.inTimeline')} style={{
                        flexShrink: 0, fontSize: 9.5, fontWeight: 800, color: '#fde68a',
                    }}>▶</span>
                )}
                {showActions && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {/* Adding a sub-branch lives in the POSSIBLE NEXT column, so
                            the current branch's row carries "Clip time" instead:
                            it trims this branch's timeline to its last keyframe. */}
                        {btn('✂', t('branch.clipTime'), () => onClip(branch.id), {
                            background: 'rgba(120,53,15,0.55)', borderColor: '#c2410c', color: '#fed7aa',
                        })}
                        {btn('✎', t('branch.edit'), () => onEdit(branch.id), {
                            background: 'rgba(6,78,59,0.6)', borderColor: '#15803d', color: '#bbf7d0',
                        })}
                        {/* Copy this branch's timeline, and paste the copied one INTO
                            this branch. Only the timeline travels — name, priority and
                            sub-branches stay put. Paste stays clickable when there is
                            nothing to paste so it can say why (the tooltip alone is
                            invisible on touch). */}
                        {btn('⧉', t('branch.copy'), () => onCopyTimeline(branch.id), {
                            background: 'rgba(30,58,138,0.55)', borderColor: '#1d4ed8', color: '#bfdbfe',
                        })}
                        {btn('⎘', !clipboardBranch
                            ? t('branch.pasteEmpty')
                            : clipboardBranch.id === branch.id
                                ? t('branch.pasteSame')
                                : t('branch.paste'),
                        () => onPasteTimeline(branch.id), {
                            background: 'rgba(30,58,138,0.55)', borderColor: '#1d4ed8', color: '#bfdbfe',
                            opacity: clipboardBranch ? 1 : 0.45,
                        })}
                        {btn('▲', t('branch.up'), () => onMove(branch.id, 'up'))}
                        {btn('▼', t('branch.down'), () => onMove(branch.id, 'down'))}
                        {/* No confirm dialog: a branch removal is an ordinary
                            undoable edit (the keyboard path never asked either),
                            and App announces it with the way back. A system
                            confirm was a native strip outside the workspace,
                            asking about something Ctrl+Z answers. */}
                        {!isRootBranch(tree, branch.id) && btn('✕', t('branch.remove'),
                            () => onRemove(branch.id), { color: '#fca5a5', borderColor: '#7f1d1d' })}
                    </span>
                )}
            </div>
        );
    };

    const col = (title, hint, childrenEl, extraStyle = {}) => (
        <div className="v3-branch-col" style={{
            flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, ...extraStyle,
        }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, paddingLeft: 2, flexShrink: 0 }}>
                <span style={{
                    fontSize: 10, fontWeight: 800, letterSpacing: '0.07em', color: '#7dd3fc',
                    whiteSpace: 'nowrap',
                }}>{title}</span>
                {hint && (
                    <span style={{ fontSize: 10, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{hint}</span>
                )}
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', gap: 4, paddingRight: 2 }}>
                {childrenEl}
            </div>
        </div>
    );

    return (
        <div className="v3-branch-panel" style={{
            position: 'absolute', left: 0, right: 0, bottom: 0, height,
            zIndex: 55, display: 'flex', flexDirection: 'column',
            background: 'rgba(2,6,23,0.97)', borderTop: '1px solid #334155',
            boxSizing: 'border-box',
        }}>
            {/* Drag handle — resize the panel exactly like the timeline strip */}
            <div
                onPointerDown={onHeightChange ? startResize : undefined}
                title={t('branch.resize')}
                style={{
                    height: 7, flexShrink: 0, cursor: onHeightChange ? 'row-resize' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    touchAction: 'none', background: 'rgba(51,65,85,0.55)',
                }}
            >
                <span style={{ width: 44, height: 2, borderRadius: 2, background: '#64748b' }} />
            </div>

            {/* The three columns */}
            <div className="v3-branch-cols" style={{
                flex: 1, minHeight: 0, display: 'flex', gap: 10, padding: '7px 10px 9px',
                overflowX: 'auto', overflowY: 'hidden',
            }}>
                {col(
                    t('branch.from'),
                    selectedIsRoot ? t('branch.rootNothingBefore') : null,
                    (ancestors.length === 0 && !selectedIsRoot &&
                        <div style={{ fontSize: 11, color: '#64748b', paddingLeft: 2, lineHeight: 1.4 }}>{t('branch.nothingBefore')}</div>)
                    || ancestors.map(a => row(a, { isRowSelected: false, showActions: false })),
                    { flex: '0 0 20%' },
                )}
                {col(
                    t('branch.current'),
                    t('branch.siblingNote'),
                    <>
                        {siblings.map(s => row(s, { isRowSelected: s.id === selected.id, showActions: s.id === selected.id }))}
                        {/* A sibling carries on from the same point as the selected
                            branch — its parent — so it belongs at the bottom of this
                            list. The root has no parent and therefore no siblings. */}
                        {!selectedIsRoot && (
                            <button
                                type="button"
                                onClick={() => onAddSibling(selected.id)}
                                title={t('branch.addSiblingHint')}
                                style={{
                                    minHeight: 32, borderRadius: 7, cursor: 'pointer', marginTop: 2,
                                    background: 'rgba(15,23,42,0.75)', color: '#c7d2fe',
                                    border: '1px dashed #6366f1', fontSize: 11.5, fontWeight: 700,
                                }}
                            >+ {t('branch.addSibling')}</button>
                        )}
                        {/* DUPLICATE THIS BRANCH — the whole thing: its timeline,
                            its name, its place, and the copy opens AND selects
                            itself so you are editing it straight away. ⧉/⎘ move a
                            timeline INTO another branch; a coach who wants "the
                            same drill, other side" wants a NEW branch, and until
                            now the only way was a chord on the keyboard. */}
                        <button
                            type="button"
                            onClick={() => onDuplicate(selected.id)}
                            title={t('branch.duplicateHint')}
                            style={{
                                minHeight: 32, borderRadius: 7, cursor: 'pointer', marginTop: 2,
                                background: 'rgba(15,23,42,0.75)', color: '#c7d2fe',
                                border: '1px dashed #6366f1', fontSize: 11.5, fontWeight: 700,
                            }}
                        >⧉ {t('branch.duplicate')}</button>
                    </>,
                    { flex: '0 0 40%' },
                )}
                {col(
                    t('branch.possible'),
                    children.length === 0 ? null : t('branch.orderHint'),
                    <>
                        {children.length === 0 && (
                            <div style={{ fontSize: 11, color: '#64748b', paddingLeft: 2, lineHeight: 1.4 }}>{t('branch.noChildren')}</div>
                        )}
                        {children.map(c => row(c, { isRowSelected: false, showActions: false }))}
                        <button
                            type="button"
                            onClick={() => onAdd(selected.id)}
                            title={t('branch.addSubHint')}
                            style={{
                                minHeight: 32, borderRadius: 7, cursor: 'pointer', marginTop: 2,
                                background: 'rgba(15,23,42,0.75)', color: '#c7d2fe',
                                border: '1px dashed #6366f1', fontSize: 11.5, fontWeight: 700,
                            }}
                        >+ {t('branch.addSub')}</button>
                    </>,
                    { flex: '1 1 32%' },
                )}
            </div>
        </div>
    );
}
